// ScraperDashboard.tsx - Scraper management dashboard
import { useState, useEffect } from 'react';
import {
  Paper,
  Stack,
  Group,
  Text,
  Title,
  Button,
  Badge,
  Card,
  Progress,
  Table,
  ActionIcon,
  Tooltip,
  Alert,
  Modal,
  Select,
} from '@mantine/core';
import {
  IconRefresh,
  IconPlayerPlay,
  IconClock,
  IconCheck,
  IconX,
  IconAlertCircle,
  IconTrash,
  IconHistory,
  IconPlus,
  IconMinus,
  IconSettings,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useScrapers, useRunScraper, useRunAllScrapers, type ScraperStatus } from '@/hooks/useScrapers';
import { useWebSocket } from '@/hooks/useWebSocket';
import { useAvailableScrapers, useSubscribeToScraper, useUnsubscribeFromScraper } from '@/hooks/useScraperSubscriptions';

export function ScraperDashboard() {
  const { data: scrapers = [], isLoading: loading, refetch } = useScrapers();
  const { data: availableScrapers = [], isLoading: loadingAvailable } = useAvailableScrapers();
  const runScraperMutation = useRunScraper();
  const runAllMutation = useRunAllScrapers();
  const subscribeMutation = useSubscribeToScraper();
  const unsubscribeMutation = useUnsubscribeFromScraper();
  const [running, setRunning] = useState<Record<string, boolean>>({});
  const [runAllModalOpen, setRunAllModalOpen] = useState(false);
  const [subscriptionModalOpen, setSubscriptionModalOpen] = useState(false);
  const [selectedSources, setSelectedSources] = useState<string[]>([]);

  // WebSocket for real-time updates
  const { isConnected, lastMessage } = useWebSocket({
    room: 'scrapers',
    onMessage: (message) => {
      if (message.type === 'scraper_status_update' || message.type === 'scraper_run_complete') {
        // Refetch scrapers when status updates
        refetch();
      }
    },
  });

  // Auto-open subscription modal for first-time users
  useEffect(() => {
    if (!loading && scrapers.length === 0 && !localStorage.getItem('scraper_modal_shown')) {
      setSubscriptionModalOpen(true);
      localStorage.setItem('scraper_modal_shown', 'true');
    }
  }, [loading, scrapers.length]);

  const runScraper = async (source: string) => {
    setRunning({ ...running, [source]: true });

    try {
      await runScraperMutation.mutateAsync(source);
      notifications.show({
        title: 'Scraper Started',
        message: `${getSourceName(source)} scraper is now running`,
        color: 'green',
      });
      // Refetch after a short delay
      setTimeout(() => refetch(), 2000);
    } catch (error: any) {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to run scraper',
        color: 'red',
      });
    } finally {
      setRunning({ ...running, [source]: false });
    }
  };

  const runAllScrapers = async () => {
    setRunning({ ...running, all: true });

    try {
      const sources = selectedSources.length > 0 ? selectedSources : undefined;
      const result = await runAllMutation.mutateAsync(sources);
      notifications.show({
        title: 'Scrapers Started',
        message: `Running ${result.totalRuns || scrapers.length} scrapers`,
        color: 'green',
      });
      setRunAllModalOpen(false);
      setSelectedSources([]);
      setTimeout(() => refetch(), 2000);
    } catch (error: any) {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to run scrapers',
        color: 'red',
      });
    } finally {
      setRunning({ ...running, all: false });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'green';
      case 'failed':
        return 'red';
      case 'running':
        return 'blue';
      default:
        return 'gray';
    }
  };

  const formatDate = (timestamp: number | null) => {
    if (!timestamp) return 'Never';
    return new Date(timestamp).toLocaleString();
  };

  const formatDuration = (started: number, completed: number | null) => {
    if (!completed) return 'Running...';
    const seconds = Math.floor((completed - started) / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    if (minutes < 60) return `${minutes}m ${remainingSeconds}s`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return `${hours}h ${remainingMinutes}m`;
  };

  const getSourceName = (source: string) => {
    const names: Record<string, string> = {
      'autoscout24': 'AutoScout24',
      'mobile-de': 'Mobile.de',
      'ebay-motors': 'eBay Motors',
      'facebook': 'Facebook Marketplace',
      'ss-com': 'SS.com',
    };
    return names[source] || source;
  };

  if (loading && scrapers.length === 0) {
    return (
      <Stack gap="md" align="center" py="xl">
        <Text size="lg" c="dimmed">Loading scrapers...</Text>
      </Stack>
    );
  }

  return (
    <Stack gap="lg">
      {/* Header */}
      <Group justify="space-between">
        <div>
          <Title order={2}>Scraper Management</Title>
          <Text c="dimmed" size="sm">
            Manage automated lead collection from external sources
          </Text>
        </div>
        <Group>
          <Badge color={isConnected ? 'green' : 'gray'} variant="light">
            {isConnected ? 'Live' : 'Offline'}
          </Badge>
          <Button
            leftSection={<IconSettings size={16} />}
            onClick={() => setSubscriptionModalOpen(true)}
            variant="outline"
          >
            Manage Subscriptions
          </Button>
          <Button
            leftSection={<IconRefresh size={16} />}
            onClick={() => refetch()}
            variant="outline"
            loading={loading}
          >
            Refresh
          </Button>
          <Button
            leftSection={<IconPlayerPlay size={16} />}
            onClick={() => setRunAllModalOpen(true)}
            loading={running.all || runAllMutation.isPending}
          >
            Run All
          </Button>
        </Group>
      </Group>

      {/* Empty State - No Subscriptions */}
      {scrapers.length === 0 && !loading && (
        <Card p="xl" radius="md" withBorder style={{ textAlign: 'center' }}>
          <Stack gap="lg" align="center">
            <IconAlertCircle size={64} style={{ color: 'var(--mantine-color-gray-5)' }} />
            <div>
              <Title order={3} mb="xs">No Scrapers Subscribed</Title>
              <Text c="dimmed" size="sm">
                Subscribe to data sources to start collecting leads automatically.
              </Text>
            </div>
            <Button
              size="lg"
              leftSection={<IconPlus size={20} />}
              onClick={() => setSubscriptionModalOpen(true)}
            >
              Subscribe to Scrapers
            </Button>
          </Stack>
        </Card>
      )}

      {/* Scraper Cards */}
      {scrapers.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1rem' }}>
          {scrapers.map((scraper) => (
          <Card key={scraper.source} p="md" radius="md" withBorder>
            <Stack gap="md">
              <Group justify="space-between">
                <div>
                  <Text fw={600} size="lg">
                    {getSourceName(scraper.source)}
                  </Text>
                  <Text size="xs" c="dimmed">
                    {scraper.source}
                  </Text>
                </div>
                {scraper.lastRun && (
                  <Badge color={getStatusColor(scraper.lastRun.status)}>
                    {scraper.lastRun.status}
                  </Badge>
                )}
              </Group>

              {scraper.lastRun && (
                <div>
                  <Group justify="space-between" mb="xs">
                    <Text size="sm" fw={600}>Last Run</Text>
                    <Text size="xs" c="dimmed">
                      {formatDate(scraper.lastRun.started_at)}
                    </Text>
                  </Group>
                  {scraper.lastRun.completed_at && (
                    <Text size="xs" c="dimmed" mb="xs">
                      Duration: {formatDuration(scraper.lastRun.started_at, scraper.lastRun.completed_at)}
                    </Text>
                  )}
                  <Stack gap="xs">
                    <Group justify="space-between">
                      <Text size="xs">Ads Found:</Text>
                      <Text size="xs" fw={600}>
                        {scraper.lastRun.ads_found}
                      </Text>
                    </Group>
                    <Group justify="space-between">
                      <Text size="xs">New:</Text>
                      <Badge size="sm" color="green" variant="light">
                        {scraper.lastRun.ads_new}
                      </Badge>
                    </Group>
                    <Group justify="space-between">
                      <Text size="xs">Modified:</Text>
                      <Badge size="sm" color="blue" variant="light">
                        {scraper.lastRun.ads_modified}
                      </Badge>
                    </Group>
                    {(scraper.lastRun.ads_failed || 0) > 0 && (
                      <Group justify="space-between">
                        <Text size="xs">Failed:</Text>
                        <Badge size="sm" color="red" variant="light">
                          {scraper.lastRun.ads_failed || 0}
                        </Badge>
                      </Group>
                    )}
                  </Stack>
                </div>
              )}

              {scraper.state && (
                <div>
                  <Stack gap="xs">
                    {scraper.state.last_success_at && (
                      <Group justify="space-between">
                        <Text size="xs" c="dimmed">Last Success:</Text>
                        <Text size="xs" c="dimmed">
                          {formatDate(scraper.state.last_success_at)}
                        </Text>
                      </Group>
                    )}
                    {scraper.state.last_run_at && (
                      <Group justify="space-between">
                        <Text size="xs" c="dimmed">Last Run:</Text>
                        <Text size="xs" c="dimmed">
                          {formatDate(scraper.state.last_run_at)}
                        </Text>
                      </Group>
                    )}
                  </Stack>
                </div>
              )}

              {!scraper.lastRun && !scraper.state && (
                <Text size="xs" c="dimmed" ta="center" py="sm">
                  No runs yet
                </Text>
              )}

              <Button
                fullWidth
                leftSection={<IconPlayerPlay size={16} />}
                onClick={() => runScraper(scraper.source)}
                loading={running[scraper.source] || runScraperMutation.isPending}
                disabled={running[scraper.source] || runScraperMutation.isPending}
              >
                Run Now
              </Button>
            </Stack>
          </Card>
          ))}
        </div>
      )}

      {/* Run All Modal */}
      <Modal
        opened={runAllModalOpen}
        onClose={() => setRunAllModalOpen(false)}
        title="Run All Scrapers"
        centered
      >
        <Stack gap="md">
          <Text size="sm">
            Select which scrapers to run, or leave empty to run all:
          </Text>
          <Select
            label="Scrapers"
            placeholder="Select scrapers (optional)"
            data={scrapers.map(s => ({
              value: s.source,
              label: getSourceName(s.source),
            }))}
            value={selectedSources.join(',')}
            onChange={(value) => setSelectedSources(value ? value.split(',') : [])}
            multiple
            searchable
          />
          <Group justify="flex-end" mt="md">
            <Button variant="subtle" onClick={() => setRunAllModalOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={runAllScrapers}
              loading={running.all}
              leftSection={<IconPlayerPlay size={16} />}
            >
              Run {selectedSources.length > 0 ? `${selectedSources.length} Selected` : 'All'}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Subscription Management Modal */}
      <Modal
        opened={subscriptionModalOpen}
        onClose={() => setSubscriptionModalOpen(false)}
        title="Manage Scraper Subscriptions"
        size="lg"
        centered
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            Subscribe to scrapers to start collecting leads from those sources. Only subscribed scrapers will appear in your dashboard and collect data for your company.
          </Text>
          
          {loadingAvailable ? (
            <Stack gap="md" align="center" py="xl">
              <Text c="dimmed">Loading available scrapers...</Text>
            </Stack>
          ) : availableScrapers.length === 0 ? (
            <Stack gap="md" align="center" py="xl">
              <IconAlertCircle size={40} color="gray" />
              <Text c="dimmed">No scrapers available at the moment.</Text>
            </Stack>
          ) : (
            <Stack gap="sm">
              {availableScrapers.map((scraper) => (
                <Paper 
                  key={scraper.source} 
                  p="md" 
                  radius="md" 
                  withBorder
                  style={{ opacity: scraper.available ? 1 : 0.6 }}
                >
                  <Group justify="space-between">
                    <div style={{ flex: 1 }}>
                      <Group gap="xs">
                        <Text fw={600}>{scraper.name}</Text>
                        {!scraper.available && (
                          <Badge color="gray" variant="light">Coming Soon</Badge>
                        )}
                      </Group>
                      {scraper.description && (
                        <Text size="xs" c="dimmed" mt={4}>
                          {scraper.description}
                        </Text>
                      )}
                      <Text size="xs" c="dimmed" mt={4}>
                        Source: {scraper.source}
                      </Text>
                    </div>
                    <Group>
                      {scraper.subscribed ? (
                        <>
                          <Badge color={scraper.enabled ? 'green' : 'gray'}>
                            {scraper.enabled ? 'Subscribed' : 'Disabled'}
                          </Badge>
                          <Button
                            size="sm"
                            variant="subtle"
                            color="red"
                            leftSection={<IconMinus size={16} />}
                            onClick={() => {
                              unsubscribeMutation.mutate(scraper.source);
                            }}
                            loading={unsubscribeMutation.isPending}
                          >
                            Unsubscribe
                          </Button>
                        </>
                      ) : (
                        <Button
                          size="sm"
                          leftSection={<IconPlus size={16} />}
                          onClick={() => {
                            subscribeMutation.mutate(scraper.source);
                          }}
                          loading={subscribeMutation.isPending}
                          disabled={!scraper.available}
                        >
                          Subscribe
                        </Button>
                      )}
                    </Group>
                  </Group>
                </Paper>
              ))}
            </Stack>
          )}
          
          <Group justify="flex-end" mt="md">
            <Button variant="subtle" onClick={() => setSubscriptionModalOpen(false)}>
              Close
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
